import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../helpers/usePageTitle';
import { getDashboardAnalytics } from '../services/apiService';
import Skeleton from 'react-loading-skeleton';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { getErrorMessage } from "../helpers/apiError";

// ── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ title, value, icon, color, suffix = '' }) => (
  <div className="bg-white rounded-[12px] p-[24px] shadow-[0px_2px_12px_0px_rgba(0,61,63,0.08)] flex items-center gap-[16px]">
    <div
      className="w-[52px] h-[52px] rounded-full flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: `${color}18` }}
    >
      <span style={{ color }} className="text-[22px]">{icon}</span>
    </div>
    <div className="min-w-0">
      <p className="text-[#809FB8] text-[13px] font-[400] leading-[18px] truncate">{title}</p>
      <p className="text-[#003D3F] text-[26px] font-[700] leading-[34px] mt-[2px]">
        {value !== null && value !== undefined ? `${value}${suffix}` : <Skeleton width={60} />}
      </p>
    </div>
  </div>
);

// ── Custom Tooltip ────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-[#D9E1E7] rounded-[10px] px-[16px] py-[12px] shadow-[0px_4px_16px_rgba(0,0,0,0.1)]">
        <p className="text-[#003D3F] text-[12px] font-[600] mb-[6px]">{label}</p>
        {payload.map((entry) => (
          <p key={entry.dataKey} className="text-[12px] font-[500]" style={{ color: entry.color }}>
            {entry.name}: <span className="font-[700]">{entry.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// ── Recent Leads Table ────────────────────────────────────────────────────────
const statusColor = {
  CONNECTED: { bg: '#0BB88518', text: '#0BB885' },
  NOT_CONNECTED: { bg: '#FF833C18', text: '#FF833C' },
  PENDING: { bg: '#809FB818', text: '#809FB8' },
};

const msgStatusColor = {
  SENT: { bg: '#0BB88518', text: '#0BB885' },
  NOT_SENT: { bg: '#D9E1E7', text: '#809FB8' },
  REPLIED: { bg: '#003D3F18', text: '#003D3F' },
};

const RecentLeadsTable = ({ leads, loading, t }) => (
  <div className="bg-white rounded-[12px] shadow-[0px_2px_12px_0px_rgba(0,61,63,0.08)] overflow-hidden">
    <div className="px-[24px] py-[20px] border-b border-[#D9E1E7CC]">
      <h3 className="text-[#003D3F] text-[16px] font-[600]">{t('dashboard.recent_leads')}</h3>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-[#F8FAFB]">
            <th className="text-start text-[#809FB8] text-[12px] font-[500] px-[24px] py-[12px]">{t('dashboard.name')}</th>
            <th className="text-start text-[#809FB8] text-[12px] font-[500] px-[24px] py-[12px]">{t('dashboard.company')}</th>
            <th className="text-start text-[#809FB8] text-[12px] font-[500] px-[24px] py-[12px]">{t('dashboard.connection')}</th>
            <th className="text-start text-[#809FB8] text-[12px] font-[500] px-[24px] py-[12px]">{t('dashboard.message')}</th>
            <th className="text-start text-[#809FB8] text-[12px] font-[500] px-[24px] py-[12px]">{t('dashboard.date')}</th>
          </tr>
        </thead>
        <tbody>
          {loading && !leads.length
            ? Array.from({ length: 4 }).map((_, i) => (
                <tr key={i} className="border-t border-[#D9E1E7CC]">
                  {Array.from({ length: 5 }).map((__, j) => (
                    <td key={j} className="px-[24px] py-[14px]"><Skeleton /></td>
                  ))}
                </tr>
              ))
            : leads.length === 0
            ? (
                <tr>
                  <td colSpan={5} className="px-[24px] py-[32px] text-center text-[#809FB8] text-[14px]">
                    {t('dashboard.no_recent_leads')}
                  </td>
                </tr>
              )
            : leads.map((lead) => {
                const conn = statusColor[lead.connectionStatus] || statusColor.NOT_CONNECTED;
                const msg = msgStatusColor[lead.messageStatus] || msgStatusColor.NOT_SENT;
                return (
                  <tr key={lead.id} className="border-t border-[#D9E1E7CC] hover:bg-[#F8FAFB] transition-colors">
                    <td className="px-[24px] py-[14px] text-[#003D3F] text-[14px] font-[500]">
                      {lead.firstName} {lead.lastName}
                    </td>
                    <td className="px-[24px] py-[14px] text-[#809FB8] text-[14px]">
                      {lead.company || '—'}
                    </td>
                    <td className="px-[24px] py-[14px]">
                      <span
                        className="inline-flex items-center px-[10px] py-[4px] rounded-full text-[12px] font-[500]"
                        style={{ backgroundColor: conn.bg, color: conn.text }}
                      >
                        {lead.connectionStatus?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-[24px] py-[14px]">
                      <span
                        className="inline-flex items-center px-[10px] py-[4px] rounded-full text-[12px] font-[500]"
                        style={{ backgroundColor: msg.bg, color: msg.text }}
                      >
                        {lead.messageStatus?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-[24px] py-[14px] text-[#809FB8] text-[13px]">
                      {new Date(lead.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                );
              })}
        </tbody>
      </table>
    </div>
  </div>
);

// ── Dashboard ─────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const { t } = useTranslation();
  usePageTitle('dashboard');

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    getDashboardAnalytics()
      .then((res) => {
        setData(res.data);
        setError(null);
      })
      .catch((err) => {
        setError(getErrorMessage(err, t('dashboard.load_error')));
      })
      .finally(() => setLoading(false));
  }, [t]);

  const overview = data?.overview || {};
  const recentLeads = data?.recent?.leads || [];
  const dailyStats = (data?.dailyStats || []).map((d) => ({
    ...d,
    date: d.date.slice(5), // show MM-DD only
  }));

  // Stat cards config
  const statCards = [
    {
      key: 'totalLeads',
      title: t('dashboard.total_leads'),
      value: overview.totalLeads,
      icon: '👥',
      color: '#003D3F',
    },
    {
      key: 'totalCampaigns',
      title: t('dashboard.total_campaigns'),
      value: overview.totalCampaigns,
      icon: '📣',
      color: '#0BB885',
    },
    {
      key: 'activeCampaigns',
      title: t('dashboard.active_campaigns'),
      value: overview.activeCampaigns,
      icon: '▶️',
      color: '#FF833C',
    },
    {
      key: 'totalConnections',
      title: t('dashboard.total_connections'),
      value: overview.totalConnections,
      icon: '🔗',
      color: '#809FB8',
    },
    {
      key: 'totalMessages',
      title: t('dashboard.total_messages'),
      value: overview.totalMessages,
      icon: '💬',
      color: '#003D3F',
    },
    {
      key: 'connectionRate',
      title: t('dashboard.connection_rate'),
      value: overview.connectionRate !== undefined ? Number(overview.connectionRate).toFixed(1) : null,
      icon: '📈',
      color: '#0BB885',
      suffix: '%',
    },
    {
      key: 'messageRate',
      title: t('dashboard.message_rate'),
      value: overview.messageRate !== undefined ? Number(overview.messageRate).toFixed(1) : null,
      icon: '📊',
      color: '#FF833C',
      suffix: '%',
    },
  ];

  return (
    <div className="space-y-[28px]">
      {/* Page Header */}
      <div>
        <h1 className="text-[#003D3F] text-[24px] font-[700]">{t('dashboard.title')}</h1>
        <p className="text-[#809FB8] text-[14px] mt-[4px]">{t('dashboard.subtitle')}</p>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-[10px] px-[20px] py-[14px] text-[14px]">
          {error}
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-[16px]">
        {loading && !data
          ? Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="bg-white rounded-[12px] p-[24px] shadow-[0px_2px_12px_0px_rgba(0,61,63,0.08)]">
                <Skeleton circle width={52} height={52} />
                <Skeleton className="mt-[12px]" width={80} height={14} />
                <Skeleton width={60} height={26} />
              </div>
            ))
          : statCards.map((card) => (
              <StatCard key={card.key} {...card} />
            ))}
      </div>

      {/* Daily Stats Chart */}
      <div className="bg-white rounded-[12px] shadow-[0px_2px_12px_0px_rgba(0,61,63,0.08)] p-[24px]">
        <h3 className="text-[#003D3F] text-[16px] font-[600] mb-[20px]">{t('dashboard.daily_activity')}</h3>
        {loading && !data ? (
          <Skeleton height={280} />
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={dailyStats} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <defs>
                <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#003D3F" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#003D3F" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorConnections" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0BB885" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#0BB885" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorMessages" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FF833C" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#FF833C" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#D9E1E7" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fill: '#809FB8', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval={4}
              />
              <YAxis
                tick={{ fill: '#809FB8', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: 13, paddingTop: 12, color: '#003D3F' }}
                iconType="circle"
                iconSize={8}
              />
              <Area
                type="monotone"
                dataKey="leads"
                name={t('dashboard.leads_label')}
                stroke="#003D3F"
                strokeWidth={2}
                fill="url(#colorLeads)"
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0 }}
              />
              <Area
                type="monotone"
                dataKey="connections"
                name={t('dashboard.connections_label')}
                stroke="#0BB885"
                strokeWidth={2}
                fill="url(#colorConnections)"
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0 }}
              />
              <Area
                type="monotone"
                dataKey="messages"
                name={t('dashboard.messages_label')}
                stroke="#FF833C"
                strokeWidth={2}
                fill="url(#colorMessages)"
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Recent Leads */}
      <RecentLeadsTable leads={recentLeads} loading={loading} t={t} />
    </div>
  );
};

export default Dashboard;
